import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import { useUsers } from '../../context/UsersContext';
import { useCheckTypes, useWorkflowStatus } from '../../hooks/useMasterData';
import Tooltip from '../../components/Tooltip';
import UserTimestamp from '../../components/UserTimestamp';
import { formatDateUS, formatDateTime, formatDateTimeTooltip } from '../../utils/dateUtils';
import { Info } from 'lucide-react';
import { MdRestoreFromTrash } from 'react-icons/md';
import '../CheckDetails.css';

const CheckTrashDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { data: checkTypes } = useCheckTypes(true);
  const { data: workflowStatuses } = useWorkflowStatus(true);
  const { getUserName, getUserById } = useUsers();

  const [check, setCheck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Restore confirm
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Permanent delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Batch sort
  const [batchSortField, setBatchSortField] = useState('batchDate');
  const [batchSortDirection, setBatchSortDirection] = useState('desc');

  // Clarification comments expand
  const [expandedCommentsClarificationId, setExpandedCommentsClarificationId] = useState(null);

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getTrashCheckDetails(id);
      setCheck(data);
    } catch (err) {
      console.error('Error fetching trash check details:', err);
      const msg = err?.message || err?.data?.message || 'Failed to load check details.';
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreConfirm = async () => {
    setIsRestoring(true);
    try {
      await api.restoreTrashCheck(id);
      showSuccess(`Check "${check?.checkNumber || 'Unknown'}" restored successfully`);
      setShowRestoreConfirm(false);
      navigate('/admin/check-trash');
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to restore check.';
      showError(msg);
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await api.permanentDeleteTrashCheck(id);
      showSuccess(`Check "${check?.checkNumber || 'Unknown'}" permanently deleted`);
      setShowDeleteConfirm(false);
      navigate('/admin/check-trash');
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to permanently delete check.';
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

  const getStatusClass = (status) => {
    const map = { 'COMPLETED': 'status-complete', 'UNDER_CLARIFICATIONS': 'status-clarification', 'IN_PROGRESS': 'status-progress', 'NOT_STARTED': 'status-not-started', 'OVER_POSTED': 'status-over-posted' };
    return map[status] || 'status-not-started';
  };

  const formatStatus = (status) => {
    const map = { 'COMPLETED': 'Completed', 'UNDER_CLARIFICATIONS': 'Under Clarifications', 'IN_PROGRESS': 'In Progress', 'NOT_STARTED': 'Not Started', 'OVER_POSTED': 'Over Posted' };
    return map[status] || status || '';
  };

  const TruncatedText = ({ text, maxLength = 15 }) => {
    if (!text) return <span>-</span>;
    if (text.length > maxLength) {
      return (
        <Tooltip text={text} position="bottom">
          <span className="truncated-text">{text.substring(0, maxLength)}...</span>
        </Tooltip>
      );
    }
    return <span>{text}</span>;
  };

  const getSortedBatches = () => {
    if (!check?.batches) return [];
    return [...check.batches].sort((a, b) => {
      let aVal = a[batchSortField] || '';
      let bVal = b[batchSortField] || '';
      if (batchSortField === 'batchAmount') { aVal = a.batchAmount || 0; bVal = b.batchAmount || 0; }
      if (batchSortField === 'batchDate' || batchSortField === 'updatedAt') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      }
      if (batchSortDirection === 'asc') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    });
  };

  const handleSortBatches = (field) => {
    if (batchSortField === field) setBatchSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setBatchSortField(field); setBatchSortDirection('asc'); }
  };

  const getSortArrow = (field) => {
    if (batchSortField !== field) return '';
    return batchSortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  if (loading) {
    return (
      <div className="check-details-page">
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading check details...</div>
      </div>
    );
  }

  if (error || !check) {
    return (
      <div className="check-details-page">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p style={{ color: '#dc2626', marginBottom: '16px' }}>{error || 'Check not found.'}</p>
          <button className="back-btn" onClick={() => navigate('/admin/check-trash')}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Check Trash
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="check-details-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-top-row">
            <button className="back-btn" onClick={() => navigate('/admin/check-trash')}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back
            </button>
            <div className="title-row">
              <h1 className="page-title">{check.checkNumber || 'Check Details'}</h1>
              <span className={`status-badge-large ${getStatusClass(check.status)}`}>{formatStatus(check.status)}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '12px', background: '#fef3c7', border: '1px solid #fbbf24', color: '#92400e', fontSize: '11px', fontWeight: 600 }}>
                🗑 In Trash
              </span>
            </div>
          </div>
          <div className="metadata">
            <span>Payer: {check.payer || 'N/A'}</span>
            <span>Practice: {check.practiceCode || 'N/A'}</span>
            <span>Location: {check.locationCode || 'N/A'}</span>
            <span>Bank TRN Ref: {check.bankTrnRef || 'N/A'}</span>
            {check.deletedAt && (
              <span style={{ color: '#dc2626' }}>
                Deleted: {formatDateTime(check.deletedAt)}
              </span>
            )}
          </div>
        </div>
        <div className="header-right">
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className="btn-secondary"
              onClick={() => setShowRestoreConfirm(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#10b981', borderColor: '#10b981' }}
            >
              <MdRestoreFromTrash size={16} />
              Restore
            </button>
          </div>
        </div>
      </div>

      {/* Check Summary Card */}
      <div className="financial-summary-card merged">
        <div className="financial-sections">
          <div className="financial-section check-summary-section" style={{ width: '100%' }}>
            <h3 className="section-title">Check Summary</h3>
            <div className="financial-grid check-summary-grid-two-rows">
              <div className="financial-item">
                <span className="financial-label">Deposit date</span>
                <span className="financial-value">{check.depositDate ? formatDateUS(check.depositDate) : 'N/A'}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Received date</span>
                <span className="financial-value">{check.receivedDate ? formatDateUS(check.receivedDate) : 'N/A'}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Complete date</span>
                <span className="financial-value">{check.completedDate ? formatDateUS(check.completedDate) : 'N/A'}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Total amount</span>
                <span className={`financial-value ${(check?.totalAmount || 0) < 0 ? 'negative' : 'primary'}`}>{formatCurrency(check?.totalAmount || 0)}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Posted amount</span>
                <span className={`financial-value ${(check?.postedAmount || 0) < 0 ? 'negative' : 'success'}`}>{formatCurrency(check?.postedAmount || 0)}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Updated date</span>
                <span className="financial-value" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  {check.updatedAt ? (
                    <>
                      {formatDateUS(check.updatedAt.split('T')[0])}
                      <Tooltip
                        text={check.updatedBy ? `${getUserName(check.updatedBy)} - ${formatDateTimeTooltip(check.updatedAt)}` : formatDateTimeTooltip(check.updatedAt)}
                        position="top"
                      >
                        <Info size={16} style={{ color: '#374151', cursor: 'pointer', flexShrink: 0 }} onMouseEnter={e => e.target.style.color = '#0d9488'} onMouseLeave={e => e.target.style.color = '#374151'} />
                      </Tooltip>
                    </>
                  ) : 'N/A'}
                </span>
              </div>
              <div className="financial-item">
                <span className="financial-label">TAT by received date</span>
                <span className="financial-value">{typeof check.turnaroundByReceivedDays === 'number' ? `${check.turnaroundByReceivedDays} day${check.turnaroundByReceivedDays === 1 ? '' : 's'}` : 'N/A'}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">TAT by deposit date</span>
                <span className="financial-value">{typeof check.turnaroundByDepositDays === 'number' ? `${check.turnaroundByDepositDays} day${check.turnaroundByDepositDays === 1 ? '' : 's'}` : 'N/A'}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Other Amount</span>
                <span className={`financial-value ${(check?.otherAmount || 0) < 0 ? 'negative' : ''}`}>{formatCurrency(check?.otherAmount || 0)}</span>
              </div>
              <div className="financial-item">
                <span className="financial-label">Remaining amount</span>
                <span className={`financial-value ${(check?.remainingAmount || 0) < 0 ? 'negative' : ''}`}>{formatCurrency(check?.remainingAmount || 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          <button className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>Overview</button>
          <button className={`tab ${activeTab === 'batches' ? 'active' : ''}`} onClick={() => setActiveTab('batches')}>Batches ({check.batches?.length || 0})</button>
          <button className={`tab ${activeTab === 'clarifications' ? 'active' : ''}`} onClick={() => setActiveTab('clarifications')}>Clarifications ({check.clarifications?.length || 0})</button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="details-card">
              <div className="card-header-with-action">
                <h3 className="card-title">Check Details</h3>
                <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>View only</span>
              </div>

              {/* Row 1 */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Alt. Check Number</label>
                  <input type="text" value={check.altCheckNumber || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Check Type</label>
                  <input type="text" value={checkTypes.find(ct => ct.code === check.checkType)?.name || check.checkType || ''} disabled />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label>Batch Description</label>
                  <input type="text" value={check.batchDescription || ''} disabled />
                </div>
              </div>

              {/* Row 2 */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Exchange</label>
                  <input type="text" value={check.exchange || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Assignee</label>
                  <input type="text" value={getUserName(check.assigneeId) || 'N/A'} disabled />
                </div>
                <div className="form-group">
                  <label>Reporter</label>
                  <input type="text" value={getUserName(check.reporterId) || 'N/A'} disabled />
                </div>
                <div className="form-group">
                  <label>Workflow Status</label>
                  <input type="text" value={check.workflowStatus ? (workflowStatuses.find(ws => ws.code === check.workflowStatus)?.name || check.workflowStatus) : 'N/A'} disabled />
                </div>
              </div>

              {/* Row 3: Transfers */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Transfer In From</label>
                  <input type="text" value={check.transferInFrom || 'N/A'} disabled />
                </div>
                <div className="form-group">
                  <label>Transfer In Amount</label>
                  <input type="text" value={formatCurrency(check.transferInAmount || 0)} disabled />
                </div>
                <div className="form-group">
                  <label>Transfer Out From</label>
                  <input type="text" value={check.transferOutFrom || 'N/A'} disabled />
                </div>
                <div className="form-group">
                  <label>Transfer Out Amount</label>
                  <input type="text" value={formatCurrency(check.transferOutAmount || 0)} disabled />
                </div>
              </div>

              {/* Row 4: Amounts */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Interest Amount</label>
                  <input type="text" value={formatCurrency(check.interestAmount)} disabled />
                </div>
                <div className="form-group">
                  <label>Non-AR Amount</label>
                  <input type="text" value={formatCurrency(check.nonArAmount)} disabled />
                </div>
                <div className="form-group">
                  <label>Medical Records Fee</label>
                  <input type="text" value={formatCurrency(check.medicalRecordsFee)} disabled />
                </div>
                <div className="form-group">
                  <label>IOD Amount</label>
                  <input type="text" value={formatCurrency(check.iodAmount || 0)} disabled />
                </div>
              </div>

              {/* Row 5: More Amounts */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Pharmacy Payment Amount</label>
                  <input type="text" value={formatCurrency(check.pharmacyPaymentAmount || 0)} disabled />
                </div>
                <div className="form-group">
                  <label>Corrections Amount</label>
                  <input type="text" value={formatCurrency(check.correctionsAmount || 0)} disabled />
                </div>
                <div className="form-group">
                  <label>Miscellaneous Amount</label>
                  <input type="text" value={formatCurrency(check.miscPaymentAmount || 0)} disabled />
                </div>
                <div className="form-group">
                  <label>Other Amount</label>
                  <input type="text" value={formatCurrency(check.otherAmount || 0)} disabled />
                </div>
              </div>

              {/* Row 6: Notes */}
              <div className="form-grid form-row-1">
                <div className="form-group">
                  <label>Legacy Notes</label>
                  <textarea value={check.legacyNotes || ''} disabled rows="2" />
                </div>
              </div>
              {check.againstCheckAdditional && (
                <div className="form-grid form-row-1">
                  <div className="form-group">
                    <label>Against Check Additional</label>
                    <textarea value={check.againstCheckAdditional || ''} disabled rows="2" />
                  </div>
                </div>
              )}

              {/* Payer / Bank TRN Ref */}
              <div className="form-grid form-row-4">
                <div className="form-group">
                  <label>Payer</label>
                  <input type="text" value={check.payer || ''} disabled />
                </div>
                <div className="form-group">
                  <label>Bank TRN Ref</label>
                  <input type="text" value={check.bankTrnRef || ''} disabled />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batches Tab */}
        {activeTab === 'batches' && (
          <div className="tab-content">
            <div className="batches-card">
              <div className="card-header">
                <h3 className="card-title">Batches</h3>
                <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>View only</span>
              </div>
              <div className="batches-table-wrapper">
                <table className="batches-table">
                  <thead>
                    <tr>
                      <th className="sortable" onClick={() => handleSortBatches('batchType')}>Batch Type{getSortArrow('batchType')}</th>
                      <th className="sortable" onClick={() => handleSortBatches('batchNumber')}>Batch Number{getSortArrow('batchNumber')}</th>
                      <th className="sortable" onClick={() => handleSortBatches('batchRunNumber')}>Run Number{getSortArrow('batchRunNumber')}</th>
                      <th>Notes</th>
                      <th className="sortable" onClick={() => handleSortBatches('edmNumber')}>EDM Number{getSortArrow('edmNumber')}</th>
                      <th className="sortable" onClick={() => handleSortBatches('batchAmount')}>Amount{getSortArrow('batchAmount')}</th>
                      <th>Correction Batch</th>
                      <th className="sortable" onClick={() => handleSortBatches('updatedAt')}>Updated At{getSortArrow('updatedAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {check.batches && check.batches.length > 0 ? (
                      getSortedBatches().map(batch => (
                        <tr key={batch.batchId} className={batch.archived ? 'batch-archived' : ''}>
                          <td>{batch.batchType || 'N/A'}</td>
                          <td><TruncatedText text={batch.batchNumber} maxLength={15} /></td>
                          <td><TruncatedText text={batch.batchRunNumber} maxLength={15} /></td>
                          <td><TruncatedText text={batch.batchNotes} maxLength={15} /></td>
                          <td><TruncatedText text={batch.edmNumber} maxLength={15} /></td>
                          <td>{formatCurrency(batch.batchAmount)}</td>
                          <td>
                            <span className={`batch-correction-badge ${batch.correctionBatch ? 'correction-yes' : 'correction-no'}`}>
                              {batch.correctionBatch ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <UserTimestamp userId={batch.updatedBy} dateTime={batch.updatedAt} action="updated" getUserName={getUserName} getUserById={getUserById} />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={8} className="empty-state">No batches found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Clarifications Tab */}
        {activeTab === 'clarifications' && (
          <div className="tab-content">
            <div className="clarifications-card">
              <div className="card-header">
                <h3 className="card-title">Clarifications</h3>
                <span style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic' }}>View only</span>
              </div>

              {check.clarifications && check.clarifications.length > 0 ? (
                <div className="clarifications-list-redesigned">
                  {check.clarifications.map(clarification => (
                    <div key={clarification.clarificationId} className="clarification-item">
                      <div className="clarification-row-1">
                        <div className="clarification-main-info">
                          <span className="clarification-type-label">Type:</span>
                          <span className="clarification-type">{clarification.clarificationType || 'N/A'}</span>
                          <span className={`clarification-status ${clarification.status?.toLowerCase()}`}>{clarification.status || 'OPEN'}</span>
                          <span className="clarification-field-inline">
                            <span className="field-label-inline">Amount:</span>
                            <span className="field-value-inline">{clarification.amount !== undefined && clarification.amount !== null ? clarification.amount : 0}</span>
                          </span>
                          <span className="clarification-field-inline">
                            <span className="field-label-inline">Assignee:</span>
                            <span className="field-value-inline">{clarification.assignee || 'N/A'}</span>
                          </span>
                          <span className="clarification-field-inline">
                            <span className="field-label-inline">Reporter:</span>
                            <span className="field-value-inline">{clarification.reportee || 'N/A'}</span>
                          </span>
                          <span className="clarification-field-inline">
                            <span className="field-label-inline">Assignee Name:</span>
                            <span className="field-value-inline">{getUserName(clarification.assigneeId) || 'N/A'}</span>
                          </span>
                          <span className="clarification-field-inline">
                            <span className="field-label-inline">Reporter Name:</span>
                            <span className="field-value-inline">{getUserName(clarification.reporterId) || 'N/A'}</span>
                          </span>
                          {clarification.pageNumber && (
                            <span className="clarification-field-inline">
                              <span className="field-label-inline">Page #:</span>
                              <span className="field-value-inline">{clarification.pageNumber}</span>
                            </span>
                          )}
                          {clarification.patientName && (
                            <span className="clarification-field-inline">
                              <span className="field-label-inline">Patient Name:</span>
                              <span className="field-value-inline">{clarification.patientName}</span>
                            </span>
                          )}
                          {clarification.patientAccountNumber && (
                            <span className="clarification-field-inline">
                              <span className="field-label-inline">Patient Acct #:</span>
                              <span className="field-value-inline">{clarification.patientAccountNumber}</span>
                            </span>
                          )}
                          <span className="clarification-field-inline">
                            <span className="field-label-inline">Opened:</span>
                            <span className="field-value-inline">{clarification.openedAt ? formatDateUS(clarification.openedAt.split('T')[0]) : 'N/A'}</span>
                          </span>
                          {clarification.resolvedAt && (
                            <span className="clarification-field-inline">
                              <span className="field-label-inline">Resolved:</span>
                              <span className="field-value-inline">{formatDateUS(clarification.resolvedAt.split('T')[0])}</span>
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="clarification-row-2">
                        <div className="clarification-details-full">
                          <span className="field-label-inline">Details:</span>
                          <span className="field-value-inline">{clarification.details || 'No details provided'}</span>
                        </div>
                      </div>

                      {/* Comments (view only) */}
                      <div className="clarification-row-3">
                        <button
                          className="comments-toggle-btn-below"
                          onClick={() => setExpandedCommentsClarificationId(
                            expandedCommentsClarificationId === clarification.clarificationId ? null : clarification.clarificationId
                          )}
                        >
                          <span className="comments-label">Comments</span>
                          <span className="comments-count">({clarification.comments?.length || 0})</span>
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="none" className={`comments-arrow ${expandedCommentsClarificationId === clarification.clarificationId ? 'expanded' : ''}`}>
                            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        {expandedCommentsClarificationId === clarification.clarificationId && (
                          <div className="comments-section-expanded">
                            <div className="comments-list">
                              {clarification.comments && clarification.comments.length > 0 ? (
                                clarification.comments.map(comment => (
                                  <div key={comment.commentId} className="comment-item-message">
                                    <div className="comment-header">
                                      <span className="comment-author">{comment.userDisplayName || 'Unknown'}</span>
                                      <span className="comment-date">{formatDateTime(comment.commentedAt)}</span>
                                    </div>
                                    <div className="comment-body">
                                      <span className="comment-text">{comment.comment}</span>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="comments-empty-state">No comments yet</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">No clarifications found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="modal-overlay" onClick={() => !isRestoring && setShowRestoreConfirm(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#111827' }}>Restore Check</h3>
            <p style={{ color: '#374151', marginBottom: '24px', lineHeight: 1.5 }}>
              Are you sure you want to restore check <strong>"{check.checkNumber}"</strong>? It will be moved back to the active checks list.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setShowRestoreConfirm(false)} disabled={isRestoring}>Cancel</button>
              <button className="btn-save" onClick={handleRestoreConfirm} disabled={isRestoring} style={{ background: '#10b981' }}>
                {isRestoring ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>Permanently Delete Check</h3>
            <p style={{ color: '#374151', marginBottom: '24px', lineHeight: 1.5 }}>
              Are you sure you want to <strong>permanently delete</strong> check <strong>"{check.checkNumber}"</strong>?{' '}
              <span style={{ color: '#dc2626', fontWeight: 600 }}>This action cannot be undone.</span>
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>Cancel</button>
              <button className="btn-save" onClick={handleDeleteConfirm} disabled={isDeleting} style={{ background: '#dc2626' }}>
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckTrashDetails;
